import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useRef } from "react";
import CashEntryServices from "../../../services/CashEntryServices";

import CashInEntryRow from "./CashInEntryRow";
import CashCategoryServices from "../../../services/CashCategoryServices";

const getId = (value) =>
    String(value?._id || value || "");

const toISTInput = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    // datetime-local requires IST wall-clock values without a timezone.
    return new Date(
        date.getTime() + 330 * 60 * 1000
    )
        .toISOString()
        .slice(0, 16);
};

const paymentModeOptions = [
    { value: "cash", label: "Cash" },
    { value: "online", label: "Online" }
];

const CashReturnedCashInEditModal = ({
    show,
    entry,
    onHide,
    onSaved
}) => {
    const [row, setRow] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const saveLock = useRef(false);

    const closeModal = () => {
        if (!saveLock.current) onHide();
    };

    useEffect(() => {
        if (!show || !entry) {
            setRow(null);
            return;
        }

        setError("");

        setRow({
            id: getId(entry),
            dateTime: toISTInput(entry.dateTime),
            categoryId: getId(entry.category),
            amount: entry.amount ?? "",
            paymentMode: entry.paymentMode || "",
            voucherNo: entry.voucherNo || "",
            remark: entry.remark || "",
            attachments: [],
            existingAttachments: Array.isArray(entry.image)
                ? [...entry.image]
                : []
        });
    }, [show, entry]);

    useEffect(() => {
        if (!show || !entry) return;

        let cancelled = false;

        const loadCategories = async () => {
            setCategoriesLoading(true);
            setCategoryOptions([]);

            try {
                const response =
                    await CashCategoryServices.getCashCategories(
                        "active",
                        "in"
                    );

                const body = response?.data?.status
                    ? response.data
                    : response;

                const categories = body?.data?.categories;

                if (!Array.isArray(categories)) {
                    throw new Error("Unexpected category response.");
                }

                const options = categories.map((category) => ({
                    value: getId(category),
                    label: category.name
                }));

                // Keep the current category visible even if now inactive.
                const currentId = getId(entry.category);

                if (
                    currentId &&
                    !options.some((option) => option.value === currentId)
                ) {
                    options.unshift({
                        value: currentId,
                        label: entry.category?.name || "Current Category"
                    });
                }

                if (!cancelled) {
                    setCategoryOptions(options);
                }
            } catch (loadError) {
                if (!cancelled) {
                    setError(
                        loadError?.response?.data?.message ||
                        loadError?.message ||
                        "Unable to load categories."
                    );
                }
            } finally {
                if (!cancelled) {
                    setCategoriesLoading(false);
                }
            }
        };

        loadCategories();

        return () => {
            cancelled = true;
        };
    }, [show, entry]);

    const updateRow = (id, field, value) => {
        setRow((previous) =>
            previous && previous.id === id
                ? { ...previous, [field]: value }
                : previous
        );
    };

    const changeAttachments = (files) => {
        const selected = Array.from(files || []);

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
            "application/pdf"
        ];

        const invalidFile = selected.find(
            (file) =>
                !(file instanceof File) ||
                !allowedTypes.includes(file.type) ||
                file.size === 0 ||
                file.size > 5 * 1024 * 1024
        );

        if (invalidFile) {
            setError(
                "Select non-empty JPG, PNG, WEBP or PDF files, up to 5 MB each."
            );
            return;
        }

        setError("");

        setRow((previous) =>
            previous
                ? { ...previous, attachments: selected }
                : previous
        );
    };

    const saveAndResubmit = async () => {
        if (saveLock.current || !row || !entry) return;

        setError("");

        const numericAmount = Number(row.amount);

        if (
            !row.dateTime ||
            !row.categoryId ||
            !Number.isFinite(numericAmount) ||
            numericAmount <= 0 ||
            !["cash", "online"].includes(row.paymentMode) ||
            !row.remark.trim()
        ) {
            setError(
                "Please enter date, category, a valid amount, payment mode and remark."
            );
            return;
        }

        const parsedDate = new Date(
            `${row.dateTime}:00+05:30`
        );

        if (Number.isNaN(parsedDate.getTime())) {
            setError("Please enter a valid date.");
            return;
        }

        saveLock.current = true;
        setSaving(true);

        try {
            let uploadedNames = [];

            if (row.attachments.length > 0) {
                const response =
                    await CashEntryServices.uploadAttachments(
                        row.attachments
                    );

                const body = response?.data?.status
                    ? response.data
                    : response;

                uploadedNames = body?.data?.filenames;

                if (
                    !Array.isArray(uploadedNames) ||
                    uploadedNames.length !== row.attachments.length ||
                    uploadedNames.some(
                        (filename) =>
                            typeof filename !== "string" ||
                            !filename.trim()
                    )
                ) {
                    throw new Error("Invalid attachment upload response.");
                }
            }

            await CashEntryServices.resubmitReturnedCashIn(
                entry._id,
                {
                    categoryId: row.categoryId,
                    amount: numericAmount,
                    paymentMode: row.paymentMode,
                    voucherNo: row.voucherNo || "",
                    remark: row.remark.trim(),
                    dateTime: parsedDate.toISOString(),
                    image: [
                        ...row.existingAttachments,
                        ...uploadedNames
                    ]
                }
            );
        } catch (saveError) {
            setError(
                saveError?.response?.data?.message ||
                saveError?.message ||
                "Unable to resubmit entry."
            );
            return;
        } finally {
            saveLock.current = false;
            setSaving(false);
        }

        onHide();
        onSaved?.();
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            size="xl"
            centered
            backdrop="static"
            keyboard={!saving}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Edit Returned Cash In
                    {entry?.requestNumber
                        ? ` — ${entry.requestNumber}`
                        : ""}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="cash-return-edit-body">
                {entry?.returnReason && (
                    <div className="alert alert-warning">
                        <strong>Return Reason:</strong>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                            {entry.returnReason}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {row && (
                    <>
                        <CashInEntryRow
                            row={row}
                            disabled={saving}
                            loading={categoriesLoading}
                            categoryOptions={categoryOptions}
                            paymentModeOptions={paymentModeOptions}
                            updateRow={updateRow}
                            canRemoveRow={false}
                            attachmentsLoading={saving}
                            onAttachmentsChange={changeAttachments}
                        />

                        {row.existingAttachments.length > 0 && (
                            <div className="border rounded p-3 mt-3">
                                <h6>Existing Attachments</h6>

                                <ul className="mb-0">
                                    {row.existingAttachments.map(
                                        (filename, index) => (
                                            <li
                                                key={`${filename}-${index}`}
                                                className="d-flex justify-content-between align-items-center gap-2 mb-2"
                                                style={{ overflowWrap: "anywhere" }}
                                            >
                                                <span>{filename}</span>

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    disabled={saving}
                                                    onClick={() =>
                                                        setRow((previous) => ({
                                                            ...previous,
                                                            existingAttachments:
                                                                previous.existingAttachments.filter(
                                                                    (_, fileIndex) => fileIndex !== index
                                                                )
                                                        }))
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                        )}
                    </>
                )}

            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="secondary"
                    disabled={saving}
                    onClick={closeModal}
                >
                    Close
                </Button>

                <Button
                    variant="primary"
                    disabled={saving || categoriesLoading || !row}
                    onClick={saveAndResubmit}
                >
                    {saving ? "Submitting..." : "Save & Resubmit"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CashReturnedCashInEditModal;